import { useMemo } from 'react';
import { Utility } from '@nkr/services/utility';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { ICprScoreAudit } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: ICprScoreAudit[],
    isAdmin: boolean,
    compatibleCount?: number,
    includedCount?: number,
    transplantedCount?: number
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const pinnedFooterRowData = [
        {
            recipientAlias: 'footer',
            // transplantDate: 'count: ' + props.data.length,
            // patientTypeDescription: '# Qualified Compatible:',
            isCompatiblePair: props.compatibleCount || 0,
            isIncluded: props.includedCount || 0,
            isTransplanted: props.transplantedCount || 0
        }
    ];

    const gridOptions = {
        getRowStyle: (params: any) => {
            if (params.node.rowPinned) {
                return {
                    backgroundColor: '#CCC',
                    fontWeight: 'bold',
                    color: '#000'
                };
            }

        }
    };

    const cols: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            width: 50,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                return params.data.recipientAlias == null ? null : params.node.rowIndex + 1;
            }
        },
        {
            colId: 'transplantDate',
            field: 'transplantDate',
            headerName: 'Transplant Date',
            width: 125,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transplant Date</SortableHeaderComponent>,
            comparator: Utility.dateComparator,
        },
        {
            colId: 'invoiceDate',
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            width: 125,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Invoice Date</SortableHeaderComponent>,
            comparator: Utility.dateComparator,
        },
        {
            colId: 'intendedDonorAlias',
            field: 'intendedDonorAlias',
            headerName: 'Intended Donor Alias',
            width: 155,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended<br />Donor Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ICprScoreAudit;

                if (params.node.isRowPinned() == true) {
                    return null;
                }

                if (!data.hasIntendedDonor) {
                    return <>--</>;
                }

                return <>{params.value}</>;
            },
        },
        {
            colId: 'intendedDonorAbo',
            field: 'intendedDonorAbo',
            headerName: 'Paired Donor ABO',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended<br />Donor ABO</SortableHeaderComponent>,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            width: 155,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ICprScoreAudit;

                if (params.node.isRowPinned()) {
                    return null;
                }

                return params.value;
            }
        },
        {
            colId: 'recipientAbo',
            field: 'recipientAbo',
            headerName: 'Paired Donor ABO',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />ABO</SortableHeaderComponent>,
        },
        {
            colId: 'isDirectDonation',
            field: 'isDirectDonation',
            headerName: 'Is Direct',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Direct<br />Donation?</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;
                return <>{d.isDirectDonation ? <span className='label label-success'>Yes</span> : <span className='label label-warning'>No</span>}</>;
            }
        },
        {
            colId: 'isMicrositeFacilitated',
            field: 'isMicrositeFacilitated',
            headerName: 'Has Microsite',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Had<br />Microsite?</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;
                return <>{d.isMicrositeFacilitated ? <span className='label label-success'>Yes</span> : <span className='label label-warning'>No</span>}</>;
            }
        },
        {
            colId: 'epletMm',
            field: 'epletMm',
            headerName: 'Eplet MM',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
        },
        {
            colId: 'patientTypeDescription',
            field: 'patientTypeDescription',
            headerName: 'Patient Type',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Type</SortableHeaderComponent>,
        },
        {
            colId: 'isCompatiblePair',
            field: 'isCompatiblePair',
            headerName: 'Compatible',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Compatible<br />Pair</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;
                return <>{d.isCompatiblePair ? <span className='label label-success'>Yes</span> : <span className='label label-warning'>No</span>}</>;
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;

                if (d.recipientAlias == 'footer') {
                    return {
                        component: 'numericRendererComponent',
                        params: {
                            total: props.compatibleCount
                        }
                    }
                }

                return null;
            }
        },
        {
            colId: 'isIncluded',
            field: 'isIncluded',
            headerName: 'Included',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Included</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;
                return <>{d.isIncluded ? <span className='label label-success'>Yes</span> : <span className='label label-warning'>No</span>}</>;
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;

                if (d.recipientAlias == 'footer') {
                    return {
                        component: 'numericRendererComponent',
                        params: {
                            total: props.includedCount
                        }
                    }
                }

                return null;
            }
        },
        {
            colId: 'isTransplanted',
            field: 'isTransplanted',
            headerName: 'Transplanted',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transplanted</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;
                return <>{d.isTranplanted ? <span className='label label-success'>Yes</span> : <span className='label label-warning'>No</span>}</>;
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                let d: ICprScoreAudit = params.data;

                if (d.recipientAlias == 'footer') {
                    return {
                        component: 'numericRendererComponent',
                        params: {
                            total: props.transplantedCount
                        }
                    }
                }

                return null;
            }
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap' height={props.isAdmin ? '77vh' : null}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                pinnedBottomRowData={pinnedFooterRowData}
                headerHeight={45}
                groupHeaderHeight={45}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No Transplants for the last 6 months...</div>}
                gridOptions={gridOptions}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onRowDataChanged={onRowDataChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;